import { createFileRoute, Outlet, redirect, useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import { useState, type CSSProperties } from "react";
import { LockKeyhole, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { BottomNav } from "@/components/site/BottomNav";
import { LoginMembershipPrompt } from "@/components/membership/LoginMembershipPrompt";
import { Button } from "@/components/ui/button";
import { canUseWorkspace, workspaceForPath } from "@/lib/workspace-view";
import { getSessionInfo } from "@/lib/session.functions";
import "@/owner-suite.css";

// Client-only gate: the session lives in localStorage, which SSR cannot read.
export const Route = createFileRoute("/_authenticated")({
  ssr: false,
  beforeLoad: async ({ location }) => {
    // A stalled authoritative lookup must not hang the route forever: bound it so
    // the guard fails closed onto the public sign-in boundary.
    const looked = await Promise.race([
      supabase.auth.getUser().then((result) => ({ settled: true as const, ...result })),
      new Promise<{ settled: false }>((resolve) =>
        setTimeout(() => resolve({ settled: false }), 8000),
      ),
    ]);
    const user = looked.settled ? looked.data?.user : undefined;
    if (!user) {
      // Do not carry a possibly stale local session into protected routes when the
      // authoritative user lookup fails or times out. Sign-out is best-effort
      // because an auth outage must still fail closed and redirect to the public
      // sign-in boundary.
      const { data: local } = await supabase.auth.getSession();
      if (local.session) await supabase.auth.signOut({ scope: "local" }).catch(() => undefined);
      throw redirect({ to: "/auth", search: { redirect: location.href } });
    }
    return { user };
  },
  component: AuthenticatedLayout,
});

// Keep these values in lockstep with owner-suite.css. The owner workspace uses
// a deliberately high-contrast pearl / navy / gold palette on every route.
const ownerTheme = {
  "--background": "#FBFCFF",
  "--foreground": "#071A3D",
  "--surface": "#F4F7FB",
  "--card": "#FFFFFF",
  "--card-foreground": "#071A3D",
  "--popover": "#FFFFFF",
  "--popover-foreground": "#071A3D",
  "--primary": "#153E80",
  "--primary-foreground": "#FFFFFF",
  "--primary-soft": "#EAF0F8",
  "--primary-deep": "#09234B",
  "--secondary": "#EEF3F8",
  "--secondary-foreground": "#102A43",
  "--muted": "#F0F4F8",
  "--muted-foreground": "#506782",
  "--accent": "#F7EDD9",
  "--accent-foreground": "#4D3810",
  "--destructive": "#C94747",
  "--destructive-foreground": "#FFFFFF",
  "--success": "#2F7D5B",
  "--success-foreground": "#FFFFFF",
  "--warning": "#C28A2E",
  "--warning-foreground": "#241A08",
  "--gold": "#D7AE65",
  "--gold-foreground": "#2B210D",
  "--navy": "#071A38",
  "--navy-foreground": "#FFFFFF",
  "--border": "#D9E2EC",
  "--input": "#C8D4E0",
  "--ring": "#153E80",
  "--shadow-premium": "0 1px 2px rgb(7 26 61 / 5%), 0 18px 46px -30px rgb(7 26 61 / 24%)",
} as CSSProperties;

function AuthenticatedLayout() {
  const { user } = Route.useRouteContext();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const workspace = workspaceForPath(pathname);
  const protectedWorkspace = workspace === "owner" || (workspace === "partner" && !pathname.startsWith("/partner/apply"));
  const sessionQuery = useQuery({
    queryKey: ["session-info", user.id],
    queryFn: () => getSessionInfo(),
    retry: false,
    enabled: protectedWorkspace,
  });

  if (!user.email_confirmed_at) {
    return <VerifyEmailScreen email={user.email ?? ""} />;
  }

  if (protectedWorkspace && sessionQuery.isLoading) {
    return <div className="flex min-h-screen items-center justify-center bg-background p-6 text-sm text-muted-foreground">Checking workspace access…</div>;
  }

  if (protectedWorkspace && (sessionQuery.error || !sessionQuery.data || !canUseWorkspace(workspace, sessionQuery.data))) {
    return <WorkspaceAccessDenied workspace={workspace} isSignedIn={Boolean(user)} />;
  }

  const membershipWorkspace = workspace === "owner" ? "owner" : workspace === "partner" ? "partner" : "customer";
  const loginSessionKey = `${user.id}:${user.last_sign_in_at ?? "session"}`;

  return (
    <div
      className={`shields-auth-shell min-h-screen ${workspace === "owner" ? "bg-[#FBFCFF] text-[#071A3D]" : "bg-background text-foreground"}`}
      data-workspace={workspace}
      style={workspace === "owner" ? ownerTheme : undefined}
    >
      <Outlet />
      <BottomNav workspace={workspace} />
      <LoginMembershipPrompt workspace={membershipWorkspace} sessionKey={loginSessionKey} />
    </div>
  );
}

function VerifyEmailScreen({ email }: { email: string }) {
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  async function resend() {
    setBusy(true);
    const { error } = await supabase.auth.resend({ type: "signup", email });
    setBusy(false);
    if (error) toast.error(error.message);
    else toast.success("Confirmation email sent — check your inbox and spam folder.");
  }

  async function checkAgain() {
    const { data, error } = await supabase.auth.getUser();
    if (error) {
      toast.error("We couldn't verify your account. Please sign in again.");
      return;
    }
    if (data.user?.email_confirmed_at) window.location.reload();
    else toast.info("Not confirmed yet — click the link in the email first.");
  }

  async function signOut() {
    await queryClient.cancelQueries();
    queryClient.clear();
    await supabase.auth.signOut();
    navigate({ to: "/auth", replace: true });
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-12">
      <div className="card-surface w-full max-w-md p-8 text-center">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
          <MailCheck className="h-7 w-7" aria-hidden />
        </span>
        <h1 className="mt-5 text-xl font-bold">Confirm your email</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          We sent a confirmation link to <span className="font-semibold text-foreground">{email}</span>.
          Click it to activate your account — you can't use Shields until your email is verified.
        </p>
        <div className="mt-6 grid gap-2">
          <Button onClick={checkAgain}>I've confirmed my email</Button>
          <Button variant="outline" onClick={resend} disabled={busy}>{busy ? "Sending…" : "Resend confirmation email"}</Button>
          <Button variant="ghost" onClick={signOut}>Sign out</Button>
        </div>
      </div>
    </div>
  );
}

function WorkspaceAccessDenied({ workspace, isSignedIn }: { workspace: "customer" | "partner" | "owner"; isSignedIn: boolean }) {
  const navigate = useNavigate();
  const title = workspace === "owner" ? "Owner access required" : "Partner approval required";
  const detail = workspace === "owner"
    ? "This workspace is restricted to the Shields owner account."
    : "Partner tools unlock only after your application has been approved. You can continue your application or return to Shields Home.";
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="card-surface w-full max-w-md p-7 text-center">
        <LockKeyhole className="mx-auto h-8 w-8 text-primary" aria-hidden />
        <h1 className="mt-4 text-xl font-bold">{title}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{detail}</p>
        <div className="mt-6 grid gap-2">
          {workspace === "partner" ? <Button onClick={() => navigate({ to: "/partner/apply" })}>Open Partner application</Button> : null}
          <Button variant={workspace === "partner" ? "outline" : "default"} onClick={() => navigate({ to: "/customer" })}>Open Shields Home</Button>
          {!isSignedIn ? <Button variant="ghost" onClick={() => navigate({ to: "/auth" })}>Sign in</Button> : null}
        </div>
      </div>
    </div>
  );
}
